import { useState } from "react";
import { ArrowRight, CheckCircle2, Zap, ShieldCheck, Wallet, Sparkles, Home, Snowflake, ThermometerSun, Award, Check, Upload, Lock } from "lucide-react";
import AidesPACWizard from "@/components/AidesPACWizard";
import DocumentsOnlyModal from "@/components/DocumentsOnlyModal";
import AccompagnementBadge from "@/components/AccompagnementBadge";

const AIDES = [
  {
    icon: Wallet,
    title: "MaPrimeRénov'",
    desc: "Jusqu'à 5 000€ selon vos revenus et le type de PAC installée.",
  },
  {
    icon: Zap,
    title: "Prime CEE",
    desc: "Certificats d'Économies d'Énergie — cumulable avec MaPrimeRénov'.",
  },
  {
    icon: ShieldCheck,
    title: "TVA réduite à 5,5%",
    desc: "Sur la fourniture et la pose par un artisan RGE certifié.",
  },
  {
    icon: Home,
    title: "Éco-PTZ",
    desc: "Prêt à taux zéro jusqu'à 15 000€ pour votre reste à charge.",
  },
];

const BENEFITS = [
  { icon: ThermometerSun, title: "70% d'économies", desc: "Sur votre facture de chauffage annuel." },
  { icon: Snowflake, title: "Chauffage & climatisation", desc: "Une PAC air-air rafraîchit aussi en été." },
  { icon: Sparkles, title: "Rendement 3 à 5×", desc: "1 kWh consommé = 3 à 5 kWh de chaleur." },
];

export default function Particuliers() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  return (
    <div data-testid="page-particuliers" className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white pt-32 pb-24 md:pt-40 md:pb-32">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 25% 30%, rgba(249,115,22,0.35) 0%, transparent 50%), radial-gradient(ellipse at 75% 70%, rgba(16,185,129,0.25) 0%, transparent 50%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0055A4] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-slate-700 font-semibold">Particuliers · Aides PAC</span>
              <span className="hidden sm:inline text-slate-400">·</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.22em] text-slate-600 font-semibold">Partenariat Rénobat</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.02] mb-6">
              Jusqu&apos;à <span className="text-[#0055A4]">10 000€ d&apos;aides</span><br className="hidden sm:block"/>pour votre pompe à chaleur.
            </h1>
            <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mb-10">
              MaPrimeRénov&apos;, prime CEE, Éco-PTZ, TVA à 5,5%... ECONATION vérifie
              votre éligibilité à toutes les aides <strong className="text-slate-900">en 2 minutes</strong>,
              gratuitement et sans engagement.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => setWizardOpen(true)}
                data-testid="particuliers-cta-eligibilite"
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#0055A4] to-[#003D7A] hover:from-[#003D7A] hover:to-[#0055A4] px-7 py-4 text-slate-900 text-sm sm:text-base font-semibold tracking-wide shadow-[0_20px_60px_-15px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 transition"
              >
                <Sparkles className="h-4 w-4" />
                Vérifier mes aides PAC
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={() => setDocsOpen(true)}
                data-testid="particuliers-cta-documents"
                className="group inline-flex items-center gap-3 rounded-full bg-slate-100 hover:bg-slate-100 backdrop-blur border border-slate-200 hover:border-white/40 px-7 py-4 text-slate-900 text-sm sm:text-base font-semibold tracking-wide transition"
              >
                <Upload className="h-4 w-4" />
                Déposer mes documents
              </button>
            </div>

            {/* Reassuring message under buttons */}
            <div data-testid="particuliers-trust-message" className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-slate-500 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
                Sans engagement · Réponse 24h
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-[#10B981]" />
                Transfert chiffré HTTPS
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#10B981]" />
                Données confidentielles · RGPD
              </div>
            </div>
            <p className="mt-4 text-slate-500 text-xs sm:text-sm max-w-2xl leading-relaxed">
              Déjà en contact avec un conseiller ECONATION ? Utilisez le bouton
              <strong className="text-slate-600"> « Déposer mes documents » </strong>
              pour transmettre directement vos pièces (avis d&apos;imposition, CNI, justificatif de domicile)
              en toute sécurité.
            </p>
          </div>

          {/* Metrics */}
          <div className="mt-16 grid grid-cols-3 gap-6 sm:gap-10 max-w-xl">
            {[
              { k: "10K€", l: "d'aides max" },
              { k: "70%", l: "d'économies" },
              { k: "24h", l: "de délai" },
            ].map((m) => (
              <div key={m.k}>
                <div className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">{m.k}</div>
                <div className="text-slate-500 text-xs uppercase tracking-[0.22em] mt-2">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Excellence Badge - Trust section (institutionnel blanc) */}
      <section data-testid="particuliers-excellence" className="relative overflow-hidden bg-gradient-to-br from-white via-[#F0F7FF]/60 to-[#ECFDF5]/60 border-t border-slate-100 py-20 md:py-24">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 20% 50%, rgba(0,85,164,0.35) 0%, transparent 45%), radial-gradient(ellipse at 80% 50%, rgba(16,185,129,0.35) 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left : Premium Badge */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative w-full max-w-md">
                {/* Outer glow */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#0055A4]/20 via-[#10B981]/15 to-transparent rounded-[2rem] blur-2xl" />
                <div className="relative rounded-[2rem] border border-slate-200 bg-white shadow-lg p-8 sm:p-10 text-center overflow-hidden">
                  {/* Corner ornaments */}
                  <div className="absolute top-4 left-4 h-4 w-4 border-t-2 border-l-2 border-[#0055A4]/60" />
                  <div className="absolute top-4 right-4 h-4 w-4 border-t-2 border-r-2 border-[#0055A4]/60" />
                  <div className="absolute bottom-4 left-4 h-4 w-4 border-b-2 border-l-2 border-[#0055A4]/60" />
                  <div className="absolute bottom-4 right-4 h-4 w-4 border-b-2 border-r-2 border-[#0055A4]/60" />

                  {/* Trophy icon */}
                  <div className="relative mx-auto mb-6 h-20 w-20 rounded-full bg-gradient-to-br from-[#0055A4] via-[#0369A1] to-[#10B981] grid place-items-center shadow-[0_20px_60px_-10px_rgba(0,85,164,0.4)]">
                    <Award className="h-10 w-10 text-white" strokeWidth={2.2} />
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#0055A4] font-bold mb-2">
                    Excellence Client
                  </div>

                  <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight mb-3">
                    2024 · 2025 · 2026
                  </div>

                  <div className="mx-auto h-px w-16 bg-gradient-to-r from-transparent via-[#0055A4]/60 to-transparent mb-4" />

                  <div className="text-slate-800 text-sm sm:text-base font-medium mb-1">
                    Accompagnement des particuliers
                  </div>
                  <div className="text-slate-500 text-xs sm:text-sm">
                    Pompes à chaleur · CEE · MaPrimeRénov&apos;
                  </div>
                </div>
              </div>
            </div>

            {/* Right : Copy + checkmarks */}
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#0055A4]/25 bg-[#0055A4]/[0.08] px-3 py-1.5 mb-5">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#0055A4] font-bold">Référence</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-5">
                Référence de l&apos;accompagnement en <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0055A4] to-[#10B981]">rénovation énergétique</span>.
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                <strong className="text-slate-900">ECONATION</strong> — spécialiste de l&apos;accompagnement PAC, CEE
                et MaPrimeRénov&apos;. En partenariat avec <strong className="text-slate-900">Rénobat</strong>, nous
                vous guidons de la vérification d&apos;éligibilité jusqu&apos;à la mise en service de votre équipement.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5">
                {[
                  "Des centaines de particuliers accompagnés",
                  "Installation clé en main",
                  "Aucune avance sur les aides éligibles",
                  "Accompagnement administratif de A à Z",
                ].map((t) => (
                  <li key={t} data-testid={`excellence-point-${t.substring(0,10).replace(/\s+/g,'-')}`} className="flex items-start gap-3">
                    <div className="mt-0.5 h-5 w-5 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 grid place-items-center flex-shrink-0">
                      <Check className="h-3 w-3 text-[#10B981]" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 text-sm sm:text-base leading-snug">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Aides */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#0055A4] uppercase mb-5">Les aides mobilisables</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Cumulables. Cumulez-les toutes.
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              La plupart des aides sont cumulables. Nos experts identifient toutes celles auxquelles
              vous avez droit pour maximiser votre financement.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AIDES.map((a) => {
              const Ic = a.icon;
              return (
                <div key={a.title} data-testid={`aide-${a.title}`} className="group rounded-2xl border border-black/[0.06] bg-white p-6 hover:border-[#0055A4]/30 hover:shadow-[0_20px_60px_-20px_rgba(249,115,22,0.25)] transition-all">
                  <div className="h-11 w-11 rounded-xl bg-[#0055A4]/10 text-[#0055A4] grid place-items-center mb-4 group-hover:bg-[#0055A4] group-hover:text-slate-900 transition-colors">
                    <Ic className="h-5 w-5" />
                  </div>
                  <div className="font-display text-lg font-bold text-[#0F172A] mb-2">{a.title}</div>
                  <p className="text-sm text-[#475569] leading-relaxed">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Accompagnement officiel MaPrimeRénov' × Anah */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <AccompagnementBadge theme="light" />
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#F8FAFC] to-white border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">La PAC en bref</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Pourquoi c&apos;est le meilleur investissement énergétique du moment.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BENEFITS.map((b) => {
              const Ic = b.icon;
              return (
                <div key={b.title} className="rounded-3xl bg-white p-8 text-slate-900 hover:-translate-y-1 transition-transform">
                  <Ic className="h-6 w-6 text-[#0055A4] mb-6" />
                  <div className="font-display text-2xl font-bold tracking-tight mb-3">{b.title}</div>
                  <p className="text-slate-500 text-sm leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0055A4] to-[#003D7A] p-10 sm:p-16 md:p-20 text-center">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4), transparent 40%)" }} />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-6 max-w-2xl mx-auto">
                Découvrez le montant de vos aides en 2 minutes.
              </h2>
              <p className="text-slate-700 text-base sm:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                12 questions, une estimation personnalisée, un accompagnement dédié.
              </p>
              <button
                onClick={() => setWizardOpen(true)}
                data-testid="particuliers-cta-final"
                className="group inline-flex items-center gap-3 rounded-full bg-white text-[#0F172A] hover:bg-white/95 px-8 py-4 text-sm sm:text-base font-bold tracking-wide shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)] hover:-translate-y-0.5 transition"
              >
                <Sparkles className="h-4 w-4" />
                Tester mon éligibilité
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <AidesPACWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <DocumentsOnlyModal open={docsOpen} onClose={() => setDocsOpen(false)} />
    </div>
  );
}
